import _ from "lodash";
import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Avatar, Button, EmptyState } from "@toddle-edu/ds-web";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";
import { Virtuoso } from "react-virtuoso";
import { useI18n } from "Hooks";
import classNames from "classnames";
import UserNameAvatar from "AppComponents/UserNameAvatar";
import {
  TAB_TYPES,
  useParticipantsModal,
} from "../../context/ParticipantsModalContext";
import { getSubtextString } from "../../utils/normaliseParticipant";
import ParticipantListSkeleton from "./ParticipantListSkeleton";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import classes from "./ParticipantList.scss";

/**
 * Single-row skeleton used as a placeholder inside Virtuoso itemContent
 * callbacks when per-item details haven't loaded yet. A full
 * ParticipantListSkeleton (5 rows) would cause layout jumps in a single slot.
 */
const ParticipantRowSkeleton = () => (
  <div className={classes.skeletonRow}>
    <SkeletonLoader width="36px" height="36px" borderRadius="4px" />
    <div className={classes.skeletonText}>
      <SkeletonLoader width="140px" height="14px" />
      <SkeletonLoader width="220px" height="12px" />
    </div>
  </div>
);

const AVATAR_STYLES = { size: "medium", randomizeColor: true };
const VIRTUOSO_STYLE = { height: "100%" };

const ParticipantList = ({
  isLoading,
  isSearchLoading,
  listData,
  hasNextPage,
  isFetchingMore,
  loadMore,
  gradeIds,
  gradeDetailsMap,
  gradeSearchResults,
  isGradeCountsLoading,
  onGradeRangeChanged,
  courseIds,
  courseDetailsMap,
  courseSearchResults,
  isCourseCountsLoading,
  onCourseRangeChanged,
  customDetailsMap,
  filteredActiveTabIds,
  onCustomRangeChanged,
}) => {
  const { t } = useI18n(["common"]);
  const {
    activeTab,
    searchText,
    sharedWith,
    isCustomParticipantList,
  } = useParticipantsModal();

  const isGradesTab = activeTab === TAB_TYPES.GRADES;
  const isCoursesTab = activeTab === TAB_TYPES.COURSES;
  const isStudentTab = activeTab === TAB_TYPES.STUDENTS;

  const hasSearchText = !_.isEmpty(_.trim(searchText));

  const rowClassName = classNames(classes.rowText, "text-body");
  const subtextClassName = classNames(
    classes.rowSubText,
    "text-body-s",
    "text-secondary"
  );

  const renderNoResults = ({ t, searchText }) => (
    <div className={classes.emptyStateContainer}>
      <EmptyState
        dsVersion="2.0"
        illustration={NoSearchInFilterIllustration}
        title={t("common:noResultHeading")}
        subtitle={t("common:no_results_with_label", {
          label: _.trim(searchText),
        })}
        size="small"
      />
    </div>
  );

  const renderEntityRow = useCallback(
    (id, name, subText, isCountsLoading) => (
      <div
        className={classes.participantRow}
        data-test-id={`participants-modal-row-${id}`}
      >
        <Avatar
          dsVersion="2.0"
          id={id}
          name={name}
          shape="square"
          size="medium"
        />
        <div className={rowClassName}>
          <span className={classes.rowName}>{name}</span>
          {isCountsLoading ? (
            <SkeletonLoader width="120px" height="12px" />
          ) : (
            subText && (
              <span
                className={subtextClassName}
                data-test-id={`participants-modal-row-${id}-subtitle`}
              >
                {subText}
              </span>
            )
          )}
        </div>
      </div>
    ),
    [rowClassName, subtextClassName]
  );

  const renderParticipantRow = useCallback(
    details => {
      if (isStudentTab) {
        return (
          <div className={classes.participantRow}>
            <UserNameAvatar
              dsVersion="2.0"
              id={details.id}
              name={details.name}
              shouldDynamicColor={true}
              showAvatar={true}
              profileImage={details.profileImage}
              shape="circle"
              size="medium"
              customSubText={details.subText || ""}
              profileCascadeDsVersion="2.0"
              studentAlertsConfig={{
                alertTypes: details.alertTypes,
                studentId: details.id,
              }}
              avatarStyles={AVATAR_STYLES}
              viewMode="NAME_AND_CUSTOM_SUBTEXT"
            />
          </div>
        );
      }
      return (
        <div className={classes.participantRow}>
          <Avatar
            dsVersion="2.0"
            id={details.id}
            name={details.name}
            src={details.profileImage}
            shape="circle"
            size="medium"
          />
          <div className={rowClassName}>
            <span className={classes.rowName}>{details.name}</span>
            {details.subText && (
              <span className={subtextClassName}>{details.subText}</span>
            )}
          </div>
        </div>
      );
    },
    [isStudentTab, rowClassName, subtextClassName]
  );

  const renderGradeRow = useCallback(
    (_index, id) => {
      const details = gradeDetailsMap[id];
      if (!details) return <ParticipantRowSkeleton />;
      return renderEntityRow(
        id,
        details.name,
        getSubtextString({ t, item: details, sharedWith }),
        isGradeCountsLoading
      );
    },
    [gradeDetailsMap, isGradeCountsLoading, sharedWith, t, renderEntityRow]
  );

  const renderCourseRow = useCallback(
    (_index, id) => {
      const details = courseDetailsMap[id];
      if (!details) return <ParticipantRowSkeleton />;
      return renderEntityRow(
        id,
        details.title,
        getSubtextString({ t, item: details, sharedWith }),
        isCourseCountsLoading
      );
    },
    [courseDetailsMap, isCourseCountsLoading, sharedWith, t, renderEntityRow]
  );

  const renderCustomRow = useCallback(
    (_index, id) => {
      const details = customDetailsMap[id];
      if (!details) return <ParticipantRowSkeleton />;
      return renderParticipantRow(details);
    },
    [customDetailsMap, renderParticipantRow]
  );

  const renderRegularRow = useCallback(
    (_index, item) => renderParticipantRow(item),
    [renderParticipantRow]
  );

  // Defined unconditionally (hooks cannot be called conditionally). The
  // computation cost is negligible — it just reads two booleans and returns a
  // small element; it is only rendered by the regular-tab Virtuoso instance.
  const loadMoreFooter = useCallback(() => {
    if (isFetchingMore) {
      return (
        <div className={classes.borderTop}>
          <ParticipantListSkeleton />
        </div>
      );
    }
    if (hasNextPage) {
      return (
        <div className={classes.loadMoreContainer}>
          <Button
            variant="progressive"
            type="inline"
            dsVersion="2.0"
            onClick={loadMore}
            testId="participants-load-more-btn"
          >
            {t("common:view_more")}
          </Button>
        </div>
      );
    }
    return null;
  }, [isFetchingMore, hasNextPage, loadMore, t]);

  const virtuosoComponents = useMemo(() => ({ Footer: loadMoreFooter }), [
    loadMoreFooter,
  ]);

  if (isLoading || (hasSearchText && isSearchLoading)) {
    return <ParticipantListSkeleton />;
  }

  let tabData = _.get(listData, activeTab, []);
  let itemContent = renderRegularRow;
  let rangeChanged;
  let components = virtuosoComponents;

  if (isGradesTab || isCoursesTab) {
    tabData = isGradesTab
      ? gradeSearchResults ?? gradeIds
      : courseSearchResults ?? courseIds;
    itemContent = isGradesTab ? renderGradeRow : renderCourseRow;
    rangeChanged = isGradesTab ? onGradeRangeChanged : onCourseRangeChanged;
    components = {};
  }

  if (isCustomParticipantList) {
    tabData = filteredActiveTabIds;
    itemContent = renderCustomRow;
    rangeChanged = onCustomRangeChanged;
    components = {};
  }

  if (hasSearchText && _.isEmpty(tabData)) {
    return renderNoResults({ t, searchText });
  }

  return (
    <div className={classes.listContainer}>
      <Virtuoso
        data={tabData}
        itemContent={itemContent}
        rangeChanged={rangeChanged}
        components={components}
        style={VIRTUOSO_STYLE}
      />
    </div>
  );
};

ParticipantList.propTypes = {
  isLoading: PropTypes.bool,
  isSearchLoading: PropTypes.bool,
  listData: PropTypes.object,
  hasNextPage: PropTypes.bool,
  isFetchingMore: PropTypes.bool,
  loadMore: PropTypes.func,
  gradeIds: PropTypes.arrayOf(PropTypes.string),
  gradeDetailsMap: PropTypes.object,
  gradeSearchResults: PropTypes.array,
  isGradeCountsLoading: PropTypes.bool,
  onGradeRangeChanged: PropTypes.func,
  courseIds: PropTypes.arrayOf(PropTypes.string),
  courseDetailsMap: PropTypes.object,
  courseSearchResults: PropTypes.array,
  isCourseCountsLoading: PropTypes.bool,
  onCourseRangeChanged: PropTypes.func,
  customDetailsMap: PropTypes.object,
  filteredActiveTabIds: PropTypes.arrayOf(PropTypes.string),
  onCustomRangeChanged: PropTypes.func,
};

ParticipantList.defaultProps = {
  isLoading: false,
  isSearchLoading: false,
  listData: {},
  hasNextPage: false,
  isFetchingMore: false,
  loadMore: () => {},
  gradeIds: [],
  gradeDetailsMap: {},
  gradeSearchResults: null,
  isGradeCountsLoading: false,
  onGradeRangeChanged: () => {},
  courseIds: [],
  courseDetailsMap: {},
  courseSearchResults: null,
  isCourseCountsLoading: false,
  onCourseRangeChanged: () => {},
  customDetailsMap: {},
  filteredActiveTabIds: [],
  onCustomRangeChanged: () => {},
};

export default ParticipantList;
